import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { exportSubmittals } from '@/utils/exportUtils';
import { SubmittalReviewModal } from '@/components/SubmittalReviewModal';
import { SubmittalProgressTracker } from '@/components/SubmittalProgressTracker';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SubmittalsTab = ({ projectId }) => {
  const [submittals, setSubmittals] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedSubmittal, setSelectedSubmittal] = useState(null);
  const [expandedSubmittal, setExpandedSubmittal] = useState(null);
  const [users, setUsers] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [formData, setFormData] = useState({ 
    title: '', 
    description: '', 
    assigned_to: '', 
    assigned_to_2: '',
    assigned_to_3: '',
    workflow_type: 'simultaneous',
    due_date: '', 
    priority: 'medium' 
  });
  const { user } = useAuth();

  useEffect(() => {
    fetchSubmittals();
    fetchUsers();
  }, []);

  const fetchSubmittals = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/submittals`);
      setSubmittals(response.data);
    } catch (error) {
      toast.error('Failed to load submittals');
    }
  };

  const fetchUsers = async () => {
    try {
      // Use project-specific users endpoint for non-admin users
      const response = await axios.get(`${API}/projects/${projectId}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const uploadFile = async (submittalId) => {
    if (!selectedFile) return null;

    setUploadingFile(true);
    const fileData = new FormData();
    fileData.append('file', selectedFile);
    fileData.append('document_type', 'submittals');
    fileData.append('project_id', projectId);

    try {
      const response = await axios.post(`${API}/upload`, fileData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.file_url;
    } catch (error) {
      toast.error('File upload failed. S3 may not be configured.');
      return null;
    } finally {
      setUploadingFile(false);
    }
  };

  const createSubmittal = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        assigned_to: formData.assigned_to,
        assigned_to_2: formData.assigned_to_2 || null,
        assigned_to_3: formData.assigned_to_3 || null,
        workflow_type: formData.workflow_type,
        due_date: formData.due_date,
        priority: formData.priority
      };
      const response = await axios.post(`${API}/projects/${projectId}/submittals`, payload);
      const submittalId = response.data.id;

      if (selectedFile) {
        const fileUrl = await uploadFile(submittalId);
        if (fileUrl) {
          await axios.patch(`${API}/submittals/${submittalId}`, null, { params: { file_url: fileUrl } });
        }
      }

      toast.success('Submittal created!');
      setShowCreateModal(false);
      setFormData({ title: '', description: '', assigned_to: '', assigned_to_2: '', assigned_to_3: '', workflow_type: 'simultaneous', due_date: '', priority: 'medium' });
      setSelectedFile(null);
      fetchSubmittals();
    } catch (error) {
      toast.error('Failed to create submittal');
    }
  };

  const deleteSubmittal = async (submittalId) => {
    if (!window.confirm('Are you sure you want to delete this submittal?')) return;
    try {
      await axios.delete(`${API}/submittals/${submittalId}`);
      toast.success('Submittal deleted');
      fetchSubmittals();
    } catch (error) {
      toast.error('Failed to delete submittal');
    }
  };

  const openReviewModal = (submittal) => {
    setSelectedSubmittal(submittal);
    setShowReviewModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': 
      case 'closed': 
        return 'bg-green-100 text-green-700';
      case 'rejected': 
        return 'bg-red-100 text-red-700';
      case 'resubmit': 
      case 'pending_resubmission': 
        return 'bg-amber-100 text-amber-700';
      case 'under_review': 
        return 'bg-blue-100 text-blue-700';
      default: 
        return 'bg-slate-100 text-slate-700';
    }
  };

  const formatDateMMDDYY = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
  };

  return (
    <div className="space-y-4" data-testid="submittals-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-slate-900">Submittals</h2>
        <div className="flex gap-2">
          <button
            onClick={() => exportSubmittals(submittals, 'excel')}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm"
            disabled={submittals.length === 0}
          >
            Export Excel
          </button>
          <button
            onClick={() => exportSubmittals(submittals, 'pdf')}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm"
            disabled={submittals.length === 0}
          >
            Export PDF
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-slate-900 text-white px-4 py-2 rounded-md hover:bg-slate-800"
            data-testid="create-submittal-button"
          >
            Create Submittal
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full" data-testid="submittals-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500 w-10"></th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Title</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Raised By</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Assignees</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Workflow</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Priority</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Due Date</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Closed Date</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">File</th>
              <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {submittals.map((submittal) => {
              // Determine if current user is an assignee or initiator
              const isInitiator = user?.id === submittal.assigned_by;
              const isAssignee = user?.id === submittal.assigned_to || 
                                 user?.id === submittal.assigned_to_2 || 
                                 user?.id === submittal.assigned_to_3;
              
              // Determine which reviewer number the current user is
              let reviewerNumber = null;
              if (user?.id === submittal.assigned_to) reviewerNumber = 1;
              else if (user?.id === submittal.assigned_to_2) reviewerNumber = 2;
              else if (user?.id === submittal.assigned_to_3) reviewerNumber = 3;
              
              const isSequential = submittal.workflow_type === 'sequential';
              const currentReviewer = submittal.current_reviewer || 1;
              const isPendingResubmission = submittal.status === 'pending_resubmission';
              
              // Can take action: reviewer (when it's their turn) OR initiator (when pending resubmission)
              const canTakeAction = user?.role === 'admin' || 
                               (isAssignee && !isPendingResubmission && (!isSequential || reviewerNumber === currentReviewer)) ||
                               (isInitiator && isPendingResubmission);
              
              // Build assignees display
              const assignees = [
                submittal.assigned_to_name,
                submittal.assigned_to_2_name,
                submittal.assigned_to_3_name
              ].filter(Boolean);
              
              const isExpanded = expandedSubmittal === submittal.id;
              
              // Current assignee display
              const currentAssigneeName = submittal.current_assignee_name || submittal.assigned_to_name;
              
              return (
                <React.Fragment key={submittal.id}>
                  <tr className={`border-b hover:bg-slate-50 ${isExpanded ? 'bg-slate-50' : ''}`} data-testid={`submittal-row-${submittal.id}`}>
                    {/* Expand/Collapse Button */}
                    <td className="p-4">
                      <button
                        onClick={() => setExpandedSubmittal(isExpanded ? null : submittal.id)}
                        className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${
                          isExpanded ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}
                        data-testid={`expand-submittal-${submittal.id}`}
                        title={isExpanded ? 'Hide progress' : 'Show progress tracker'}
                      >
                        <svg 
                          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </td>
                    <td className="p-4 font-medium">{submittal.title}</td>
                    <td className="p-4 text-sm">{submittal.assigned_by_name || 'Unknown'}</td>
                    <td className="p-4 text-sm">
                      <div className="space-y-1">
                        {assignees.map((name, idx) => {
                          const approvalStatus = submittal[`approval_${idx + 1}`];
                          return (
                            <div key={idx} className="flex items-center gap-2">
                              <span>{name}</span>
                              {approvalStatus && approvalStatus !== 'pending' && (
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  approvalStatus === 'approved' ? 'bg-green-100 text-green-700' :
                                  approvalStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                                  'bg-amber-100 text-amber-700'
                                }`}>
                                  {approvalStatus}
                                </span>
                              )}
                              {isSequential && currentReviewer === idx + 1 && approvalStatus === 'pending' && !isPendingResubmission && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">current</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        submittal.workflow_type === 'sequential' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-700'
                      }`}>
                        {submittal.workflow_type || 'simultaneous'}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        submittal.priority === 'high' ? 'bg-red-100 text-red-700' :
                        submittal.priority === 'medium' ? 'bg-amber-100 text-amber-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {submittal.priority}
                      </span>
                    </td>
                    <td className="p-4 text-sm">{new Date(submittal.due_date).toLocaleDateString()}</td>
                    <td className="p-4">
                      <div className="flex flex-col gap-1">
                        <span className={`px-2 py-1 rounded text-xs font-medium inline-block w-fit ${getStatusColor(submittal.status)}`}>
                          {submittal.status?.replace('_', ' ')}
                        </span>
                        {isPendingResubmission && (
                          <span className="text-xs text-slate-500">
                            Assigned to: {currentAssigneeName}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-sm" data-testid={`closed-date-${submittal.id}`}>
                      {formatDateMMDDYY(submittal.closed_date)}
                    </td>
                    <td className="p-4">
                      {submittal.file_url ? (
                        <a href={submittal.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                          View
                        </a>
                      ) : (
                        <span className="text-slate-400 text-sm">No file</span>
                      )}
                      {(submittal.attachments?.length > 0) && (
                        <span className="text-xs text-slate-500 ml-1">
                          (+{submittal.attachments.length})
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {/* Action button */}
                        {canTakeAction && (
                          <button 
                            onClick={() => openReviewModal(submittal)}
                            className={`text-sm hover:underline font-medium ${
                              isPendingResubmission && isInitiator ? 'text-blue-600' : 'text-blue-600'
                            }`}
                            data-testid={`review-submittal-${submittal.id}`}
                          >
                            {isPendingResubmission && isInitiator ? 'Respond' : 'Review'}
                          </button>
                        )}
                        {/* View button for non-actionable users */}
                        {!canTakeAction && (
                          <button 
                            onClick={() => openReviewModal(submittal)}
                            className="text-sm text-slate-600 hover:underline"
                          >
                            View
                          </button>
                        )}
                        {/* Delete button - only for admin */}
                        {user?.role === 'admin' && (
                          <button 
                            onClick={() => deleteSubmittal(submittal.id)}
                            className="text-sm text-red-600 hover:underline font-medium"
                            data-testid={`delete-submittal-${submittal.id}`}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* Expanded Progress Tracker Row */}
                  {isExpanded && (
                    <tr className="bg-slate-50">
                      <td colSpan="11" className="p-4">
                        <SubmittalProgressTracker submittal={submittal} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-submittal-modal">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Create Submittal</h2>
            <form onSubmit={createSubmittal} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Title</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" required data-testid="submittal-title-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" rows="3" data-testid="submittal-description-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Upload File</label>
                <input type="file" onChange={handleFileSelect} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" data-testid="submittal-file-input" />
                {selectedFile && <p className="text-xs text-slate-600 mt-1">Selected: {selectedFile.name}</p>}
              </div>
              
              {/* Workflow Type Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Approval Workflow</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="workflow_type" 
                      value="simultaneous"
                      checked={formData.workflow_type === 'simultaneous'}
                      onChange={(e) => setFormData({...formData, workflow_type: e.target.value})}
                      className="text-indigo-600"
                    />
                    <span className="text-sm">Simultaneous</span>
                    <span className="text-xs text-slate-500">(all review at once)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="workflow_type" 
                      value="sequential"
                      checked={formData.workflow_type === 'sequential'}
                      onChange={(e) => setFormData({...formData, workflow_type: e.target.value})}
                      className="text-indigo-600"
                    />
                    <span className="text-sm">Sequential</span>
                    <span className="text-xs text-slate-500">(one after another)</span>
                  </label>
                </div>
              </div>
              
              {/* Assignees */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Reviewer 1 (Primary) <span className="text-red-500">*</span>
                  </label>
                  <select value={formData.assigned_to} onChange={(e) => setFormData({...formData, assigned_to: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" required data-testid="submittal-assign-select">
                    <option value="">Select reviewer</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Reviewer 2 (Optional)
                  </label>
                  <select 
                    value={formData.assigned_to_2} 
                    onChange={(e) => setFormData({...formData, assigned_to_2: e.target.value})} 
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    data-testid="submittal-assign-2-select"
                  >
                    <option value="">No second reviewer</option>
                    {users.filter(u => u.id !== formData.assigned_to).map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Reviewer 3 (Optional)
                  </label>
                  <select 
                    value={formData.assigned_to_3} 
                    onChange={(e) => setFormData({...formData, assigned_to_3: e.target.value})} 
                    className="w-full px-3 py-2 border border-slate-200 rounded-md"
                    disabled={!formData.assigned_to_2}
                    data-testid="submittal-assign-3-select"
                  >
                    <option value="">No third reviewer</option>
                    {users.filter(u => u.id !== formData.assigned_to && u.id !== formData.assigned_to_2).map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </select>
                  {!formData.assigned_to_2 && <p className="text-xs text-slate-500 mt-1">Select Reviewer 2 first</p>}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Due Date</label>
                <input type="date" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" required data-testid="submittal-duedate-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Priority</label>
                <select value={formData.priority} onChange={(e) => setFormData({...formData, priority: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-md" data-testid="submittal-priority-select">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={uploadingFile} className="flex-1 bg-slate-900 text-white py-2 rounded-md hover:bg-slate-800 disabled:opacity-50" data-testid="submit-submittal-button">
                  {uploadingFile ? 'Uploading...' : 'Create'}
                </button>
                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 border border-slate-200 py-2 rounded-md hover:bg-slate-50" data-testid="cancel-submittal-button">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showReviewModal && selectedSubmittal && (
        <SubmittalReviewModal
          submittal={selectedSubmittal}
          onClose={() => setShowReviewModal(false)}
          onUpdate={fetchSubmittals}
        />
      )}
    </div>
  );
};